import Immutable from 'immutable';
import { schema } from 'normalizr';
import { createNormalizedStoreUpdater } from 'edrolo/reducers/utils';

export const actionTypes = {
  SET_PRIVILEGED_CONTENT: 'edrolo/content/SET_PRIVILEGED_CONTENT',

  LOAD_COURSE_CONTENT_SIGNAL: 'edrolo/content/LOAD_COURSE_CONTENT_SIGNAL',
  LOAD_COURSE_CONTENT_FETCH: 'edrolo/content/LOAD_COURSE_CONTENT_FETCH',
  LOAD_COURSE_CONTENT_SUCCESS: 'edrolo/content/LOAD_COURSE_CONTENT_SUCCESS',
  LOAD_COURSE_CONTENT_FAILURE: 'edrolo/content/LOAD_COURSE_CONTENT_FAILURE',

  LOAD_LESSON_SIGNAL: 'edrolo/content/LOAD_LESSON_SIGNAL',
  LOAD_LESSON_FETCH: 'edrolo/content/LOAD_LESSON_FETCH',
  LOAD_LESSON_SUCCESS: 'edrolo/content/LOAD_LESSON_SUCCESS',
  LOAD_LESSON_FAILURE: 'edrolo/content/LOAD_LESSON_FAILURE',
};

export const keys = {
  COURSE: 'coursesById',
  TOPIC: 'topics',
  EXAM: 'exams',
  LESSON: 'lessons',
  CHUNK: 'chunks',
  LESSON_RESOURCE: 'lessonResources',
  COURSE_RESOURCE: 'courseResources',
  COURSES_BY_CLASS_ID: 'coursesByClassId',
};

const defaultState = Immutable.fromJS({
  [keys.COURSE]: {},
  [keys.TOPIC]: {},
  [keys.LESSON]: {},
  [keys.CHUNK]: {},
  [keys.EXAM]: {},
  [keys.LESSON_RESOURCE]: {},
  [keys.COURSE_RESOURCE]: {},
});

// Main Reducer export
export default (state = defaultState, { type, payload }) => {
  switch (type) {
    case actionTypes.SET_PRIVILEGED_CONTENT: {
      const normalizedUpdater = createNormalizedStoreUpdater(
        [keys.EXAM, keys.LESSON_RESOURCE, keys.COURSE_RESOURCE],
        payload.entities
      );

      return normalizedUpdater(state);
    }

    case actionTypes.LOAD_LESSON_FETCH:
      return state.set('contentLoading', true);

    case actionTypes.LOAD_LESSON_SUCCESS: {
      const normalizedUpdater = createNormalizedStoreUpdater(
        [keys.LESSON, keys.CHUNK],
        payload.entities
      );

      return normalizedUpdater(state).set('contentLoading');
    }

    case actionTypes.LOAD_COURSE_CONTENT_SUCCESS: {
      const normalizedUpdater = createNormalizedStoreUpdater(
        [keys.COURSE, keys.TOPIC, keys.LESSON, keys.CHUNK],
        payload.entities
      );
      return normalizedUpdater(state).set('contentLoading');
    }

    case actionTypes.LOAD_LESSON_FAILURE:
      return state.set('contentLoading', false);

    default:
      return state;
  }
};

const setPrivilegedContent = (entities) => ({
  type: actionTypes.SET_PRIVILEGED_CONTENT,
  payload: { entities },
});

const loadCourseContent = {
  signal: (classId) => ({
    type: actionTypes.LOAD_COURSE_CONTENT_SIGNAL,
    payload: { classId },
  }),
  request: (classId) => ({
    type: actionTypes.LOAD_COURSE_CONTENT_FETCH,
    payload: { classId },
  }),
  success: (classId, entities) => ({
    type: actionTypes.LOAD_COURSE_CONTENT_SUCCESS,
    payload: {
      classId,
      entities,
    },
  }),
  failure: (errors) => ({
    type: actionTypes.LOAD_COURSE_CONTENT_FAILURE,
    payload: { errors },
  }),
};

const loadLesson = {
  signal: (lessonId) => ({
    type: actionTypes.LOAD_LESSON_SIGNAL,
    payload: { lessonId },
  }),
  request: (lessonId) => ({
    type: actionTypes.LOAD_LESSON_FETCH,
    payload: { lessonId },
  }),
  success: (lessonId, entities) => ({
    type: actionTypes.LOAD_LESSON_SUCCESS,
    payload: {
      lessonId,
      entities,
    },
  }),
  failure: (errors) => ({
    type: actionTypes.LOAD_LESSON_FAILURE,
    payload: { errors },
  }),
};

export const actions = {
  setPrivilegedContent,
  loadCourseContent,
  loadLesson,
};

export const selectors = {
  getAllCourses: (state) => state.content.get(keys.COURSE),
  getAllTopics: (state) => state.content.get(keys.TOPIC),
  getAllLessons: (state) => state.content.get(keys.LESSON),
  getAllChunks: (state) => state.content.get(keys.CHUNK),
  getAllExams: (state) => state.content.get(keys.EXAM),
  getAllLessonResources: (state) => state.content.get(keys.LESSON_RESOURCE),
  getAllCourseResources: (state) => state.content.get(keys.COURSE_RESOURCE),
};

/** Schemas **/
const idOrPk = (input) => input.pk || input.id;

const idToPk = (input) => {
  const processed = {
    ...input,
  };

  if (input.pk) {
    processed.id = input.pk;
    delete processed.pk;
  }

  return processed;
};

export const schemas = {
  course: new schema.Entity(
    keys.COURSE,
    {},
    { idAttribute: idOrPk, processStrategy: idToPk }
  ),
  topic: new schema.Entity(
    keys.TOPIC,
    {},
    { idAttribute: idOrPk, processStrategy: idToPk }
  ),
  lesson: new schema.Entity(
    keys.LESSON,
    {},
    { idAttribute: idOrPk, processStrategy: idToPk }
  ),
  chunk: new schema.Entity(
    keys.CHUNK,
    {},
    {
      idAttribute: idOrPk,
      processStrategy: (input) => {
        const processed = {
          ...input,
        };

        if (processed.pk) {
          processed.id = processed.pk;
          delete processed.pk;
        }

        if (processed.duration) {
          processed.duration = Number(processed.duration);
        }

        return processed;
      },
    }
  ),

  exam: new schema.Entity(keys.EXAM),
  lessonResource: new schema.Entity(keys.LESSON_RESOURCE),
  courseResource: new schema.Entity(keys.COURSE_RESOURCE),
};

schemas.course.define({
  exams: new schema.Array(schemas.exam),
  topics: new schema.Array(schemas.topic),
});

schemas.topic.define({
  exams: new schema.Array(schemas.exam),
  lessons: new schema.Array(schemas.lesson),
});

schemas.lesson.define({
  chunks: new schema.Array(schemas.chunk),
});
